<script>
export default {
  name: "DidoDownload",
};
</script>

<script setup>
import { computed, ref, watch } from "vue";
import DidoDownloadButtonDataset from "./download/buttons/Dataset.vue";
import DidoDownloadButtonDatafile from "./download/buttons/Datafile.vue";
import DidoDownloadButtonMillesime from "./download/buttons/Millesime.vue";
import api from "../api";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  widgetType: { type: String, required: true },
  datasetId: { type: String, required: false, default: "" },
  datafileRid: { type: String, required: false, default: "" },
  datafileMillesime: { type: String, required: false, default: "" },
});

// define events emitted
const emit = defineEmits([
  "widget-type-unknown",
  "dataset-not-retrieved",
  "datafile-not-retrieved",
  "millesime-not-retrieved",
  "dataset-retrieved",
  "datafile-retrieved",
  "millesime-retrieved",
  "bad-configuration",
]);

// define Reactive Data
const dataset = ref(null);
const datafile = ref(null);
const millesimeObject = ref(null);

// define computed data
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!["dataset", "datafile", "millesime"].includes(props.widgetType))
    return false;
  if (props.widgetType === "dataset" && !props.datasetId) return false;
  if (props.widgetType === "datafile" && !props.datafileRid) return false;
  if (
    props.widgetType === "millesime" &&
    (!props.datafileRid || !props.datafileMillesime)
  )
    return false;
  return true;
});

// define watchers
watch(
  () => [
    props.apiUrl,
    props.widgetType,
    props.datasetId,
    props.datafileRid,
    props.datafileMillesime,
  ],
  () => getDataForWidget(),
);

// define methods
const getDataForWidget = async () => {
  if (props.widgetType === "millesime") return await getMillesime();
  if (props.widgetType === "dataset") return await getDataset();
  if (props.widgetType === "datafile") return await getDatafile();
  emit("widget-type-unknown", { widgetType: props.widgetType });
};
const getDataset = async () => {
  // if widget bad configured
  if (!configured.value) {
    dataset.value = null;
    emit("bad-configuration");
    return;
  }
  await api
    .getDataset(props.apiUrl, props.datasetId)
    .catch(() => ({ dataset: null }))
    .then(({ dataset: datasetValue }) => {
      dataset.value = datasetValue;
      return datasetValue;
    })
    .then((dataset) =>
      emit(dataset ? "dataset-retrieved" : "dataset-not-retrieved", {
        datasetId: props.datasetId,
      }),
    );
};
const getDatafile = async () => {
  // if widget bad configured
  if (!configured.value) {
    datafile.value = null;
    emit("bad-configuration");
    return;
  }
  await api
    .getDatafile(props.apiUrl, props.datafileRid)
    .catch(() => ({ datafile: null }))
    .then(({ datafile: datafileValue }) => {
      datafile.value = datafileValue;
      return datafileValue;
    })
    .then((datafile) =>
      emit(datafile ? "datafile-retrieved" : "datafile-not-retrieved", {
        datafileRid: props.datafileRid,
      }),
    );
};
const getMillesime = async () => {
  // if widget bad configured
  if (!configured.value) {
    millesimeObject.value = null;
    emit("bad-configuration");
    return;
  }
  if (props.apiUrl && props.datafileRid && props.datafileMillesime) {
    await api
      .getMillesime(props.apiUrl, props.datafileRid, props.datafileMillesime)
      .catch(() => ({ millesime: null }))
      .then(({ millesimeObject: millesimeObjectValue }) => {
        millesimeObject.value = millesimeObjectValue;
        return millesimeObject;
      })
      .then((millesimeObject) =>
        emit(
          millesimeObject ? "millesime-retrieved" : "millesime-not-retrieved",
          {
            datafileRid: props.datafileRid,
            datafileMillesime: props.datafileMillesime,
          },
        ),
      );
  }
};

// Init
getDataForWidget();
</script>

<template>
  <div class="dido-widget-download relative-position">
    <!-- Bad configuration of widget -->
    <div v-if="!configured">
      <div>Impossible de charger le composant...</div>
    </div>
    <DidoDownloadButtonDataset
      v-if="props.widgetType === 'dataset' && dataset"
      :dataset="dataset"
      :api-url="props.apiUrl"
    />

    <DidoDownloadButtonDatafile
      v-if="props.widgetType === 'datafile' && datafile"
      :datafile="datafile"
      :api-url="props.apiUrl"
    />

    <DidoDownloadButtonMillesime
      v-if="props.widgetType === 'millesime' && millesimeObject"
      :millesime-object="millesimeObject"
      :api-url="props.apiUrl"
    />
  </div>
</template>
